/**
 * Market Sell View
 * Fish selling interface for markets using the modular FishSellList component.
 * Consistent UI pattern with QuestSellPanel: quantity input + "Sell N" + "Sell All" per stack.
 */

import { useGameStore } from '@/state';
import { sellManager } from '@/systems/selling/SellManager';
import { marketManager } from '@/systems';
import { Quality } from '@/types';
import { FishSellList, AutoSellThresholdControl } from '@/components/selling';
import { ScrollableView } from '@/components/layout/ScrollableView';
import { BackButton } from '@/components/layout/BackButton';

interface MarketSellViewProps {
  marketId: string;
  onBack: () => void;
}

export function MarketSellView({ marketId, onBack }: MarketSellViewProps) {
  const inventory = useGameStore((state) => state.playerState.inventory);
  const protectedFish = useGameStore((state) => state.playerState.autoSellSettings.protectedFish);
  const playerStats = useGameStore((state) => state.playerStats);

  const market = marketManager.getMarket(marketId);

  if (!market) {
    return (
      <div className="p-6 text-center text-gray-400">
        <p>Market not found.</p>
      </div>
    );
  }

  // Handle selling fish
  const handleSell = (definitionId: string, quality: Quality, count: number) => {
    sellManager.sellFish(
      [{ definitionId, quality, count }],
      marketId
    );
  };

  // Handle selling all fish (excluding protected)
  const handleSellAll = () => {
    sellManager.sellAllFish(marketId);
  };

  // Handle toggling protection
  const handleToggleProtection = (definitionId: string, quality: Quality) => {
    useGameStore.getState().toggleFishProtection(definitionId, quality);
  };

  // Calculate total sellable value
  const totalSellableValue = sellManager.calculateInventoryValue(marketId);

  return (
    <ScrollableView>
      {/* Header */}
      <div>
        <BackButton label="Market" onClick={onBack} className="mb-4" />

        <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 rounded-xl border-2 border-green-600 p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-2xl font-bold text-white">{market.name}</h1>
              <p className="text-gray-300">{market.description}</p>
            </div>
            <div className="text-4xl">🍽️</div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-gray-900/50 rounded-lg p-3">
              <div className="text-xs text-gray-400">Market Rate</div>
              <div className="text-lg font-bold text-green-400">
                {Math.round(market.fishPriceMultiplier * 100)}% of base
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-3">
              <div className="text-xs text-gray-400">Your Bonus</div>
              <div className="text-lg font-bold text-blue-400">
                +{Math.round(playerStats.valueBonus * 100)}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auto-Sell Quality Threshold */}
      <AutoSellThresholdControl />

      {/* Modular Fish Sell List */}
      <FishSellList
        stacks={inventory.stacks}
        buyerId={marketId}
        onSell={handleSell}
        protectedFish={protectedFish}
        onToggleProtection={handleToggleProtection}
        onSellAll={handleSellAll}
        totalSellableValue={totalSellableValue}
        emptyMessage="Your inventory is empty"
        emptySubMessage="Go catch some fish to sell!"
      />
    </ScrollableView>
  );
}
